// WEB-1368 п.6.6 (WEB-2090 ч.2в): connectCustomDomainThunk, purchaseDomainThunk и
// searchAvailabilityThunk сняты — экраны ходят в domainsApi напрямую, у проверки
// доступности не было даже обработчика результата. Живы fetch и переключение
// автопродления (pages/Domains.tsx).
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { domainsApi } from '@/api';
import { extractApiError } from '@/api/errorStatus';
import type { Domain, DomainsState } from '@/types';
import { rejectedApiError } from './rejectedApiError';
import type {
  CheckAvailabilityPayload,
  DomainAvailabilityResult,
} from '@/api/domains';

// Re-export types that the popup components need
export type { CheckAvailabilityPayload, DomainAvailabilityResult };

const initialState: DomainsState = {
  domains: [],
  isLoading: false,
  error: null,
};

export const fetchDomainsThunk = createAsyncThunk(
  'domains/fetchAll',
  async ({ projectId }: { projectId: string }, { rejectWithValue }) => {
    try {
      const response = await domainsApi.list(projectId);
      return response.data.domains as Domain[];
    } catch (error: unknown) {
      // WEB-1955: отказ отвергается РАЗОБРАННЫМ (статус, код, причина) —
      // текстом терялся код, а фразу человеку кабинет выбирает по коду.
      // Запасную фразу подставляет экран (localizeError), у него она на двух
      // языках; английская константа слайса была русскому человеку не фразой.
      return rejectWithValue(extractApiError(error));
    }
  },
);


export const toggleAutoRenewalThunk = createAsyncThunk(
  'domains/toggleAutoRenewal',
  async (
    {
      projectId,
      domainId,
      autoRenewal,
    }: { projectId: string; domainId: string; autoRenewal: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await domainsApi.toggleAutoRenewal(projectId, domainId, {
        autoRenewal,
      });
      // WEB-1594: сервер сообщает отказ ТЕЛОМ ответа ({ success:false, error }),
      // не исключением (domains.service.ts:toggleAutoRenewal, общий catch).
      // Раньше признак не читался, тханк завершался «успехом», и `as boolean`
      // протаскивал undefined в строку домена → переключатель рисовал
      // «выключено», хотя на сервере автопродление осталось включённым.
      // Образец — OrgDomains.handleToggleAutoRenew (WEB-1261).
      if (!response.data.success) {
        // Строку сервера НЕ переводим здесь: её показывает экран и гонит
        // через localizeError (Domains.tsx / OrgDomains.tsx) — второй
        // перевод по дороге сделал бы две двери у одного правила.
        return rejectWithValue(extractApiError(response.data.error ?? ''));
      }
      return {
        domainId,
        // При success:true без поля в теле берём запрошенное значение —
        // сервер подтвердил именно его; undefined в строку не пишем.
        autoRenewal:
          typeof response.data.autoRenewal === 'boolean'
            ? response.data.autoRenewal
            : autoRenewal,
      };
    } catch (error: unknown) {
      // WEB-1955: запасную фразу («Не удалось изменить автопродление» /
      // «Could not change auto-renewal») ставит ЭКРАН — она есть в словаре на
      // обоих языках, а слайс отдаёт разобранную причину сервера.
      return rejectWithValue(extractApiError(error));
    }
  },
);

const domainsSlice = createSlice({
  name: 'domains',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetchAll
    builder
      .addCase(fetchDomainsThunk.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        // Ф3 (ревизия десятки 3, 15.08): requestId-гард — см. DomainsState.latestRequestId.
        state.latestRequestId = action.meta?.requestId ?? null;
      })
      .addCase(fetchDomainsThunk.fulfilled, (state, action) => {
        // Ф3: a stale (superseded) response landing late must not overwrite
        // the fresher list already on screen (mirrors quizzesSlice/paywallsSlice).
        if (state.latestRequestId != null && action.meta.requestId !== state.latestRequestId) {
          return;
        }
        state.isLoading = false;
        state.domains = action.payload;
      })
      // WEB-1337 P2: a failed refetch must not leave the PREVIOUS org/
      // project's domain list on screen next to the error — that reads as
      // valid current data instead of "we don't actually know it".
      .addCase(fetchDomainsThunk.rejected, (state, action) => {
        // Ф3: same out-of-order guard as fulfilled above.
        if (state.latestRequestId != null && action.meta.requestId !== state.latestRequestId) {
          return;
        }
        state.isLoading = false;
        state.error = rejectedApiError(action);
        state.domains = [];
      });


    // WEB-1777 п.2: кейсов продления тут больше нет. Оба thunk'а (одиночное и
    // массовое продление) были мёртвым кодом — ни один компонент их не
    // диспатчил, — и при этом не несли правила несверённости, которое живые
    // двери продления соблюдают. Удалены вместе с кейсами; сторож удаления
    // живёт в domainsSlice.test.ts.

    // toggleAutoRenewal
    // WEB-1594: rejected-ветки тут больше нет НАМЕРЕННО. state.error — ошибка
    // ЗАГРУЗКИ списка: Domains.tsx по ней подменяет всю таблицу экраном
    // «Не удалось загрузить домены» (DomainsErrorState). Отказ переключателя
    // одной строки загруженный список недействительным не делает, а текст про
    // загрузку был бы ложью. Причину отказа человеку показывает вызывающий
    // (Domains.handleToggleAutoRenew, message.error из payload) и перечитывает
    // список, чтобы на экране было состояние сервера.
    builder.addCase(toggleAutoRenewalThunk.fulfilled, (state, action) => {
      state.domains = state.domains.map((d) =>
        d.id === action.payload.domainId
          ? { ...d, autoRenewal: action.payload.autoRenewal }
          : d,
      );
    });

  },
});

export default domainsSlice.reducer;
